/**
 * SWR cache + localStorage hydration.
 *
 * Persisted keys survive reloads so repeat visitors get an instant paint from
 * cached JSON while SWR revalidates in the background. In-memory keys stay
 * unprefixed so SWR mutate can match hook subscribers. localStorage keys are
 * prefixed with the catalog tier so a live payload cannot hydrate a snapshot
 * visit.
 */

import { catalogLiveTier, type CatalogTier } from '../data/catalogAuth';

const STORAGE_PREFIX = 'swr-cache:';
const VERSION = 'v1';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // drop anything older than a day

// Only top-level list pages persist across reloads; detail pages use tuple
// SWR keys (e.g. `['rate', id]`) we don't want to hydrate stale data for.
const CACHE_KEY_ALLOWLIST: readonly string[] = ['indices-list', 'rates-list'];

function isAllowed(key: string): boolean {
  return CACHE_KEY_ALLOWLIST.includes(key);
}

function persistKey(tier: CatalogTier, key: string): string {
  return `${tier}:${key}`;
}

function parsePersistedKey(stored: string): { tier: CatalogTier; key: string } | null {
  const sep = stored.indexOf(':');
  if (sep < 0) return null;
  const tier = stored.slice(0, sep);
  const key = stored.slice(sep + 1);
  if (tier !== 'live' && tier !== 'snapshot') return null;
  if (!key) return null;
  return { tier, key };
}

type Entry = { t: number; v: unknown; version: string };

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function createPersistentSWRCache(): Map<string, unknown> {
  const storage = safeStorage();
  const map = new Map<string, unknown>();

  if (storage) {
    const bootTier = catalogLiveTier();
    try {
      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
        const raw = storage.getItem(k);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw) as Entry;
          if (parsed.version !== VERSION) {
            storage.removeItem(k);
            continue;
          }
          if (Date.now() - parsed.t > MAX_AGE_MS) {
            storage.removeItem(k);
            continue;
          }
          const stored = parsePersistedKey(k.slice(STORAGE_PREFIX.length));
          if (!stored || stored.tier !== bootTier || !isAllowed(stored.key)) continue;
          map.set(stored.key, parsed.v);
        } catch {
          storage.removeItem(k);
        }
      }
    } catch {
      // ignore
    }
  }

  const originalSet = map.set.bind(map);
  const originalDelete = map.delete.bind(map);
  map.set = (key: string, value: unknown) => {
    originalSet(key, value);
    if (storage && isAllowed(key)) {
      try {
        const entry: Entry = { t: Date.now(), v: value, version: VERSION };
        storage.setItem(STORAGE_PREFIX + persistKey(catalogLiveTier(), key), JSON.stringify(entry));
      } catch {
        try {
          Object.keys(storage)
            .filter(k => k.startsWith(STORAGE_PREFIX))
            .forEach(k => storage.removeItem(k));
        } catch {
          // give up
        }
      }
    }
    return map;
  };
  map.delete = (key: string) => {
    if (storage && isAllowed(key)) {
      try {
        storage.removeItem(STORAGE_PREFIX + persistKey(catalogLiveTier(), key));
      } catch {
        // ignore
      }
    }
    return originalDelete(key);
  };

  return map;
}
